import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import useActionPanelStore from 'src/stores/useActionPanelStore';

/**
 * ModalObserver - Component that detects when Bootstrap modals are open
 * and renders its children only when a modal is open
 */
const ModalObserver = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const checkTimeoutRef = useRef(null);
  const { panelStack } = useActionPanelStore();

  useEffect(() => {
    // Function to check if any modal is open
    const checkForOpenModals = () => {
      // Check for Bootstrap modal markers
      const modalBackdropExists = document.querySelector('.modal-backdrop') !== null;
      const bodyHasModalOpen = document.body.classList.contains('modal-open');
      const anyModalHasShowClass = document.querySelector('.modal.show') !== null;
      const isActionPanelVisible = panelStack.length >= 1;

      // If any of these conditions are true, a modal is open
      setIsModalOpen(
        modalBackdropExists || bodyHasModalOpen || anyModalHasShowClass || isActionPanelVisible
      );
    };

    // Debounce function to prevent excessive checks
    const debouncedCheck = () => {
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
      checkTimeoutRef.current = setTimeout(checkForOpenModals, 50);
    };

    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(mutations => {
      // Only process mutations that might be related to modals
      const relevantChange = mutations.some(mutation => {
        // For attribute mutations, check if class was changed
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          return true;
        }

        // For added nodes, check if modal-related elements were added
        if (mutation.addedNodes.length) {
          return Array.from(mutation.addedNodes).some(node => {
            return (
              node.classList &&
              (node.classList.contains('modal-backdrop') || node.classList.contains('modal'))
            );
          });
        }

        return false;
      });

      if (relevantChange) {
        debouncedCheck();
      }
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true, // Observe direct children
      subtree: true, // And lower descendants too
      attributes: true, // Observe attribute changes
      attributeFilter: ['class'], // Only class changes
    });

    // Initial check when component mounts
    checkForOpenModals();

    // Clean up observer and timeout on component unmount
    return () => {
      observer.disconnect();
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
  }, [panelStack]);

  // Only render children when a modal is opened
  return isModalOpen ? children : null;
};

ModalObserver.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ModalObserver;
